import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getTasksOptions,
  getTasksQueryKey,
  searchTasksOptions,
  searchTasksQueryKey,
  reportTaskMutation,
  enqueueTaskMutation,
  claimTaskMutation,
} from '@/shared/api';

export interface TaskQueryParams {
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: string;
}

export interface SearchTaskParams extends TaskQueryParams {
  platform?: string;
  action?: string;
  status?: string;
  dedupeKey?: string;
}

/**
 * Query factory for tasks
 */
export const taskQueries = {
  all: (params?: TaskQueryParams) => getTasksOptions({ query: params }),
  allKey: (params?: TaskQueryParams) => getTasksQueryKey({ query: params }),
  search: (params?: SearchTaskParams) => searchTasksOptions({ query: params }),
  searchKey: (params?: SearchTaskParams) => searchTasksQueryKey({ query: params }),
};

/**
 * Hook to fetch all tasks with pagination
 */
export const useTasks = (params?: TaskQueryParams, enabled: boolean = true) => {
  return useQuery({
    ...taskQueries.all(params),
    enabled,
  });
};

/**
 * Hook to search tasks with pagination
 */
export const useSearchTasks = (params?: SearchTaskParams, enabled: boolean = true) => {
  return useQuery({
    ...taskQueries.search(params),
    enabled: enabled && !!params,
  });
};

/**
 * Hook to report task execution result
 */
export const useReportTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...reportTaskMutation(),
    onSuccess: () => {
      // Invalidate all task queries
      queryClient.invalidateQueries({ queryKey: taskQueries.allKey() });
      queryClient.invalidateQueries({ queryKey: taskQueries.searchKey() });
    },
  });
};

/**
 * Hook to enqueue a new task
 */
export const useEnqueueTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...enqueueTaskMutation(),
    onSuccess: () => {
      // Invalidate all task queries to refresh task lists
      queryClient.invalidateQueries({ queryKey: taskQueries.allKey() });
      queryClient.invalidateQueries({ queryKey: taskQueries.searchKey() });
    },
  });
};

/**
 * Hook to claim the next available task
 */
export const useClaimTask = () => {
  const queryClient = useQueryClient();

  return useMutation({
    ...claimTaskMutation(),
    onSuccess: () => {
      // Invalidate all task queries
      queryClient.invalidateQueries({ queryKey: taskQueries.allKey() });
      queryClient.invalidateQueries({ queryKey: taskQueries.searchKey() });
    },
  });
};
